import { formatDate } from "modules/common/services/formatting/date";

/**
 * Точечное сохранение состава затрат: на бэк уходят только те поля строки или платежа,
 * которые сотрудник действительно изменил. Благодаря этому правки соседних полей
 * (например, статус фин. запроса от бухгалтерии) не затираются.
 */

/** Поля строки затрат, которые сохраняются точечно */
const SPENDING_FIELDS = [
  "actualSum",
  "orderNumber",
  "startDate",
  "days",
  "taskIssued",
  "taskCompleted",
  "taskAccepted",
  "additions",
  "comment",
  "employerId",
  "outsourcerId",
  "contentGuid",
  "sortOrder",
];

/** Поля планового платежа, которые сохраняются точечно */
const PLAN_PAYMENT_FIELDS = [
  "name",
  "sum",
  "date",
  "concreteDate",
  "isCash",
  "comment",
  "employerId",
  "immediate",
  "invoice",
  "eventDates",
];

/** В форме даты живут объектами Date, на сервере и в сторе — строками */
function normalize(value: any) {
  return value instanceof Date ? formatDate(value) : value;
}

/** Значение из инпута приходит строкой, поэтому «2000» из формы сравнивается с 2000 из стора */
function numeric(value: any) {
  return typeof value === "string" && value.trim() !== "" ? Number(value) : value;
}

function changed(left: any, right: any) {
  if (left === right) {
    return false;
  }

  // null и undefined приходят вперемешку — для сравнения это одно и то же
  if (left == null && right == null) {
    return false;
  }

  // суммы и сроки в форме лежат строками, а в сторе числами
  if (typeof left === "number" || typeof right === "number") {
    const leftNumber = numeric(left);
    const rightNumber = numeric(right);

    if (typeof leftNumber === "number" && typeof rightNumber === "number") {
      return leftNumber !== rightNumber;
    }
  }

  if (typeof left === "object" || typeof right === "object") {
    return JSON.stringify(left ?? null) !== JSON.stringify(right ?? null);
  }

  return true;
}

function diff(current: any, saved: any, fields: string[]) {
  const result: TStringMap<any> = {};

  fields.forEach((field) => {
    if (!current || !(field in current)) {
      return;
    }

    const value = normalize(current[field]);

    if (changed(value, saved ? normalize(saved[field]) : undefined)) {
      result[field] = value;
    }
  });

  return result;
}

/** Изменённые поля строки затрат — без платежей, они сохраняются отдельно */
export function diffSpendingRow(current: any, saved: any) {
  return diff(current, saved, SPENDING_FIELDS);
}

/** Изменённые поля одного планового платежа */
export function diffPlanPayment(current: any, saved: any) {
  return diff(current, saved, PLAN_PAYMENT_FIELDS);
}

export function hasChanges(patch: TStringMap<any>) {
  return Object.keys(patch).length > 0;
}

/** Строка затрат целиком — для создания новой */
export function spendingFields(current: any) {
  return diff(current, {}, SPENDING_FIELDS);
}

/** Плановый платёж целиком — для создания нового */
export function planPaymentFields(current: any) {
  return diff(current, {}, PLAN_PAYMENT_FIELDS);
}
